import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { ConfiguratorDataViewService, DataViewDto, SaveQuery, Column } from '@tj/tj-dmp-lib-api';
import { TreeNode } from 'primeng/api';
import { Filter } from '../components/filter/filter.type';

export interface CompatibilityIssue {
    type: 'missing_column' | 'missing_field' | 'incompatible_constraint' | 'missing_table';
    message: string;
    details?: string;
}

export interface CompatibilityResult {
    compatible: boolean;
    issues: CompatibilityIssue[];
}

@Injectable({
    providedIn: 'root'
})
export class PresetCompatibilityService {

    constructor(
        private dataViewService: ConfiguratorDataViewService
    ) {}

    /**
     * Valide la compatibilité d'un preset avec une vue d'un autre tenant
     * @param preset Le preset à copier
     * @param targetDatastore Le datastore de destination
     * @param targetViewCode Le code de la vue de destination
     * @param targetPartnerId Le partner ID de destination (optionnel)
     * @returns Observable<CompatibilityResult>
     */
    validatePresetCompatibility(
        preset: SaveQuery,
        targetDatastore: string,
        targetViewCode: string,
        targetPartnerId?: string
    ): Observable<CompatibilityResult> {
        // Si on copie vers la même vue du même tenant, c'est forcément compatible
        if (preset.datastore === targetDatastore && preset.view === targetViewCode) {
            return of({
                compatible: true,
                issues: []
            });
        }

        return this.dataViewService.getDataViewUsingGET(targetDatastore, targetPartnerId, targetViewCode, true).pipe(
            map((targetView: DataViewDto) => {
                return this.checkCompatibility(preset, targetView);
            })
        );
    }

    /**
     * Vérifie la compatibilité entre un preset et une vue cible
     */
    private checkCompatibility(preset: SaveQuery, targetView: DataViewDto): CompatibilityResult {
        const issues: CompatibilityIssue[] = [];

        // 1. Vérifier que les colonnes displayables existent dans la vue cible
        if (preset.displayables && preset.displayables.length > 0) {
            const viewColumns = this.getViewColumnCodes(targetView);
            const missingColumns = preset.displayables.filter(col => !viewColumns.includes(col));

            if (missingColumns.length > 0) {
                issues.push({
                    type: 'missing_column',
                    message: `Colonnes manquantes dans la vue de destination`,
                    details: `Les colonnes suivantes n'existent pas dans la vue cible : ${missingColumns.join(', ')}`
                });
            }
        }

        // 2. Vérifier les champs utilisés dans les filtres (objectQueryJson)
        if (preset.objectQueryJson) {
            try {
                const filterTree: TreeNode<Filter> = JSON.parse(preset.objectQueryJson);
                const usedFields = this.extractFieldsFromFilter(filterTree);
                const viewColumns = this.getViewColumnCodes(targetView);
                const viewPropertyNames = this.getViewPropertyNames(targetView);

                // Un champ est valide s'il existe soit comme "table.colonne" soit comme "propertyName"
                const missingFields = usedFields.filter(field =>
                    !viewColumns.includes(field) && !viewPropertyNames.includes(field)
                );

                if (missingFields.length > 0) {
                    issues.push({
                        type: 'missing_field',
                        message: `Champs de filtre manquants dans la vue de destination`,
                        details: `Les champs de filtre suivants n'existent pas dans la vue cible : ${missingFields.join(', ')}`
                    });
                }
            } catch (e) {
                issues.push({
                    type: 'incompatible_constraint',
                    message: `Impossible de parser les filtres du preset`,
                    details: `Erreur lors de l'analyse des filtres : ${e.message}`
                });
            }
        }

        // 3. Vérifier les champs de tri (orderings)
        if (preset.orderings && preset.orderings.length > 0) {
            const viewColumns = this.getViewColumnCodes(targetView);
            const viewPropertyNames = this.getViewPropertyNames(targetView);
            const orderingFields = preset.orderings.map(o => o.code);

            // Un champ de tri est valide s'il existe soit comme "table.colonne" soit comme "propertyName"
            const missingOrderingFields = orderingFields.filter(field =>
                !viewColumns.includes(field) && !viewPropertyNames.includes(field)
            );

            if (missingOrderingFields.length > 0) {
                issues.push({
                    type: 'missing_field',
                    message: `Champs de tri manquants dans la vue de destination`,
                    details: `Les champs de tri suivants n'existent pas dans la vue cible : ${missingOrderingFields.join(', ')}`
                });
            }
        }

        // 4. Vérifier les tables utilisées dans les filtres "having"
        if (preset.having && preset.having.length > 0) {
            const targetHavingTables = targetView.havingCountTable || [];
            const presetHavingTables = preset.having.map(h => h.tableName).filter(t => !!t);
            const missingHavingTables = presetHavingTables.filter(table => !targetHavingTables.includes(table));

            if (missingHavingTables.length > 0) {
                issues.push({
                    type: 'missing_table',
                    message: `Tables manquantes pour les filtres "having"`,
                    details: `Les tables suivantes ne sont pas disponibles dans la vue cible : ${missingHavingTables.join(', ')}`
                });
            }
        }

        return {
            compatible: issues.length === 0,
            issues
        };
    }

    /**
     * Extrait tous les codes de colonnes disponibles dans une vue
     * Retourne les colonnes au format "table.colonne"
     */
    private getViewColumnCodes(view: DataViewDto): string[] {
        const columns = new Set<string>();

        // Fonction helper pour ajouter des colonnes
        const addColumns = (cols: Column[]) => {
            if (cols) {
                cols.forEach(col => {
                    if (col.tableName && col.propertyName) {
                        // Format: table.colonne
                        columns.add(`${col.tableName}.${col.propertyName}`);
                    }
                });
            }
        };

        // Ajouter toutes les colonnes de toutes les sources possibles
        addColumns(view.selectColumns);
        addColumns(view.defaultDisplayFields);
        addColumns(view.defaultDisplayFieldsContent);
        addColumns(view.defaultSearchFields);
        addColumns(view.defaultSortFields);
        addColumns(view.defaultFieldCreation);

        return Array.from(columns);
    }

    /**
     * Extrait tous les noms de propriétés (sans le préfixe de table) disponibles dans une vue
     * Retourne uniquement les propertyName
     */
    private getViewPropertyNames(view: DataViewDto): string[] {
        const propertyNames = new Set<string>();

        // Fonction helper pour ajouter des noms de propriétés
        const addPropertyNames = (cols: Column[]) => {
            if (cols) {
                cols.forEach(col => {
                    if (col.propertyName) {
                        propertyNames.add(col.propertyName);
                    }
                });
            }
        };

        // Ajouter tous les noms de propriétés de toutes les sources possibles
        addPropertyNames(view.selectColumns);
        addPropertyNames(view.defaultDisplayFields);
        addPropertyNames(view.defaultDisplayFieldsContent);
        addPropertyNames(view.defaultSearchFields);
        addPropertyNames(view.defaultSortFields);
        addPropertyNames(view.defaultFieldCreation);

        return Array.from(propertyNames);
    }

    /**
     * Extrait récursivement tous les champs utilisés dans un arbre de filtres
     */
    private extractFieldsFromFilter(node: TreeNode<Filter>): string[] {
        const fields: Set<string> = new Set();

        if (node?.data?.field?.name) {
            fields.add(node.data.field.name);
        }

        if (node?.children) {
            node.children.forEach(child => {
                const childFields = this.extractFieldsFromFilter(child);
                childFields.forEach(f => fields.add(f));
            });
        }

        return Array.from(fields);
    }
}
